import React, { useMemo } from "react";

/**
 * V1.7.9 — Mini-map overlay.
 *
 * Renders a tiny top-down floor plan in the bottom-right of the 3D
 * viewport along with a camera-position dot and a yellow frustum wedge
 * that rotates with the user's view direction. Helps users stay oriented
 * while orbiting / teleporting / flying through the 3D scene.
 *
 * Coordinate convention (matches FloorPlan2D + ThreeViewport):
 *  - Blueprint x maps to SVG x
 *  - Blueprint z maps to FLIPPED SVG y so the front of the house
 *    (low z, public rooms) renders at the BOTTOM of the mini-map,
 *    consistent with the Plan tab.
 *
 * Props:
 *  - blueprint:  the design's blueprint_json (must contain floors[].rooms[])
 *  - cameraPose: { x, z, yawDeg } from useCompassYaw
 *  - className:  optional positioning override
 */
const MAP_SIZE = 156;      // outer container px
const PAD = 8;
const FOV_DEG = 60;        // wedge half-angle (matches ~PerspectiveCamera fov)
const WEDGE_LEN = 26;

export default function MiniMap({ blueprint, cameraPose, onRoomClick, className = "" }) {
  const layout = useMemo(() => {
    if (!blueprint || !Array.isArray(blueprint.floors)) return null;
    // Pick the ground floor (height_offset 0..0.5) — typically floor_index 1.
    const ground =
      blueprint.floors.find(
        (f) => (f?.height_offset || 0) >= 0 && (f?.height_offset || 0) < 0.5,
      ) || blueprint.floors[0];
    const rooms = (ground?.rooms || []).filter(
      (r) =>
        r &&
        Number.isFinite(r.x) &&
        Number.isFinite(r.z) &&
        r.width > 0 &&
        r.depth > 0,
    );
    if (rooms.length === 0) return null;

    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    rooms.forEach((r) => {
      minX = Math.min(minX, r.x);
      minZ = Math.min(minZ, r.z);
      maxX = Math.max(maxX, r.x + r.width);
      maxZ = Math.max(maxZ, r.z + r.depth);
    });
    // Pad bounds slightly so the camera marker can sit JUST outside the
    // footprint when the user orbits to the property's edge.
    const margin = 4;
    minX -= margin;
    minZ -= margin;
    maxX += margin;
    maxZ += margin;
    const widthM = maxX - minX;
    const depthM = maxZ - minZ;
    const innerSize = MAP_SIZE - PAD * 2;
    const scale = Math.min(innerSize / widthM, innerSize / depthM);
    const innerW = widthM * scale;
    const innerH = depthM * scale;
    // Center the plan within the box.
    const offX = PAD + (innerSize - innerW) / 2;
    const offY = PAD + (innerSize - innerH) / 2;

    return {
      rooms,
      bounds: { minX, minZ, maxX, maxZ },
      scale,
      offX,
      offY,
      // World→SVG transforms.
      toX: (mx) => offX + (mx - minX) * scale,
      // Flip Z so front of house (low z) is at the BOTTOM of the mini-map.
      toY: (mz) => offY + (maxZ - mz) * scale,
    };
  }, [blueprint]);

  if (!layout) return null;
  const { rooms, toX, toY } = layout;

  // Camera marker. Clamp inside the visible box so the dot never escapes.
  const camX = Math.max(2, Math.min(MAP_SIZE - 2, toX(cameraPose?.x ?? 0)));
  const camY = Math.max(2, Math.min(MAP_SIZE - 2, toY(cameraPose?.z ?? 0)));
  // The yaw frustum. ThreeViewport's compass uses yaw = atan2(fwd.x, fwd.z)
  // in radians. The wedge tip points in the camera's forward direction.
  // SVG y axis is INVERTED relative to world z (we flipped). The wedge dx,dy
  // therefore use:  dx = sin(yaw),  dy = -cos(yaw).
  const yawRad = ((cameraPose?.yawDeg ?? 0) * Math.PI) / 180;
  const halfFov = (FOV_DEG * Math.PI) / 180 / 2;
  const pts = [
    [camX, camY],
    [
      camX + Math.sin(yawRad - halfFov) * WEDGE_LEN,
      camY - Math.cos(yawRad - halfFov) * WEDGE_LEN,
    ],
    [
      camX + Math.sin(yawRad + halfFov) * WEDGE_LEN,
      camY - Math.cos(yawRad + halfFov) * WEDGE_LEN,
    ],
  ];
  const wedgePoints = pts.map(([x, y]) => `${x},${y}`).join(" ");

  return (
    <div
      data-testid="mini-map"
      className={
        "absolute bottom-3 right-3 z-20 hidden md:block bg-zinc-950/85 border border-zinc-700 backdrop-blur-md " +
        className
      }
      style={{ width: MAP_SIZE, height: MAP_SIZE + 18 }}
    >
      <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-brass-400/80 border-b border-zinc-800">
        // Mini-map
      </div>
      <svg
        width={MAP_SIZE}
        height={MAP_SIZE}
        viewBox={`0 0 ${MAP_SIZE} ${MAP_SIZE}`}
        className="block"
        data-testid="mini-map-svg"
      >
        {/* Rooms */}
        {rooms.map((r) => {
          const x = toX(r.x);
          // Top edge of the room (highest z in world → lowest y in flipped svg)
          const y = toY(r.z + r.depth);
          const w = r.width * layout.scale;
          const h = r.depth * layout.scale;
          const clickable = typeof onRoomClick === "function";
          return (
            <rect
              key={r.id || `${r.x}_${r.z}`}
              x={x}
              y={y}
              width={w}
              height={h}
              fill="rgba(8, 145, 178, 0.18)"
              stroke="#155e75"
              strokeWidth={0.8}
              style={clickable ? { cursor: "pointer" } : undefined}
              onClick={
                clickable
                  ? (e) => {
                      e.stopPropagation();
                      onRoomClick({
                        id: r.id,
                        name: r.name,
                        cx: r.x + r.width / 2,
                        cz: r.z + r.depth / 2,
                      });
                    }
                  : undefined
              }
              onMouseEnter={
                clickable
                  ? (e) => {
                      e.currentTarget.setAttribute(
                        "fill",
                        "rgba(250, 204, 21, 0.28)",
                      );
                    }
                  : undefined
              }
              onMouseLeave={
                clickable
                  ? (e) => {
                      e.currentTarget.setAttribute(
                        "fill",
                        "rgba(8, 145, 178, 0.18)",
                      );
                    }
                  : undefined
              }
              data-testid={`mini-map-room-${r.id || `${r.x}_${r.z}`}`}
            >
              {r.name && <title>{r.name}</title>}
            </rect>
          );
        })}
        {/* Frustum wedge (drawn before the dot so the dot sits on top) */}
        <polygon
          points={wedgePoints}
          fill="rgba(250, 204, 21, 0.28)"
          stroke="#facc15"
          strokeWidth={1}
          strokeLinejoin="round"
          data-testid="mini-map-frustum"
        />
        {/* Camera dot */}
        <circle
          cx={camX}
          cy={camY}
          r={3.2}
          fill="#facc15"
          stroke="#0c0a09"
          strokeWidth={1}
          data-testid="mini-map-camera-dot"
        />
        {/* FRONT label at bottom edge */}
        <text
          x={MAP_SIZE / 2}
          y={MAP_SIZE - 3}
          textAnchor="middle"
          className="fill-brass-400"
          fontSize="7"
          fontFamily="JetBrains Mono, monospace"
          letterSpacing="0.1em"
        >
          FRONT
        </text>
      </svg>
    </div>
  );
}
